import { Form } from 'antd';
import type { RadioChangeEvent } from 'antd';
import { useContext } from 'react';
import { useStore } from 'zustand';

import { inventoryStore } from 'app/components/pages/Extranet/ExtranetInventory/Modern/InventoryStore';
import { ModernHotelContext } from 'app/context/HotelContext/ModernHotelContext';
import { OPENING_CONFIGURATION_TYPE } from 'app/typings/core/admin/experiences/enum/openingConfiguration';
import type { DayOfWeek } from 'app/typings/core/common/enum/dayOfWeek';
import { DAY_OF_WEEK } from 'app/typings/core/common/enum/dayOfWeek';

import { TextBlock } from '../../../../../Common/TextBlock/TextBlock';
import { TypeSelector } from '../../../../../Common/TypeSelector/TypeSelector';

import { ConstantStock } from './ConstantStock/ConstantStock';
import { DailyStock } from './DailyStock/DailyStock';

const STOCK_CONFIGURATION_FIELD = [
  'openingConfiguration',
  'stockConfiguration',
];

type StockDayConfigurationFormValue = {
  stock?: number | null;
  isClosed?: boolean;
  isDisabled?: boolean;
  isToggled?: boolean;
};

type StockConfigurationFormValue = {
  type?: OPENING_CONFIGURATION_TYPE;
  configuration?: {
    stock?: number | null;
  };
  days?: Partial<Record<DayOfWeek, StockDayConfigurationFormValue>>;
};

export const StockConfig = () => {
  const { getExperience } = useContext(ModernHotelContext);
  const experienceId = useStore(
    inventoryStore,
    (state) => state.experienceStockConfig?.expId
  );

  const experience = getExperience(experienceId);

  const form = Form.useFormInstance();
  const stockType = Form.useWatch(
    ['openingConfiguration', 'stockConfiguration', 'type'],
    form
  );

  const priceConfiguration =
    experience?.openingConfiguration?.priceConfiguration;
  const stockConfiguration =
    experience?.openingConfiguration?.stockConfiguration;

  const hasAdminDisabledDay =
    stockConfiguration?.type === OPENING_CONFIGURATION_TYPE.DAILY &&
    Object.values(DAY_OF_WEEK).some((day) => {
      const isClosed = stockConfiguration.days[day]?.isClosed;

      if (!isClosed) {
        return false;
      }

      if (!priceConfiguration) {
        return true;
      }

      if (priceConfiguration.type === OPENING_CONFIGURATION_TYPE.CONSTANT) {
        return (
          priceConfiguration.configuration.price === null ||
          priceConfiguration.configuration.price === undefined
        );
      }

      return (
        priceConfiguration.days[day as DayOfWeek]?.price === null ||
        priceConfiguration.days[day as DayOfWeek]?.price === undefined
      );
    });

  const isPriceEmptyForDay = (day: DayOfWeek) => {
    if (!priceConfiguration) {
      return true;
    }

    if (experience?.isAlwaysIncludedWithNight) {
      if (priceConfiguration.type === OPENING_CONFIGURATION_TYPE.CONSTANT) {
        return (
          priceConfiguration.configuration.daytimePrice === null ||
          priceConfiguration.configuration.daytimePrice === undefined
        );
      }

      return (
        priceConfiguration.days[day]?.daytimePrice === null ||
        priceConfiguration.days[day]?.daytimePrice === undefined
      );
    }

    if (priceConfiguration.type === OPENING_CONFIGURATION_TYPE.CONSTANT) {
      return (
        priceConfiguration.configuration.price === null ||
        priceConfiguration.configuration.price === undefined
      );
    }

    return (
      priceConfiguration.days[day]?.price === null ||
      priceConfiguration.days[day]?.price === undefined
    );
  };

  const buildDailyStockDays = (
    currentStockConfiguration?: StockConfigurationFormValue
  ) => {
    const currentConstantStock =
      currentStockConfiguration?.configuration?.stock;
    const originalConstantStock =
      stockConfiguration?.type === OPENING_CONFIGURATION_TYPE.CONSTANT
        ? stockConfiguration.configuration?.stock
        : undefined;
    let stock = originalConstantStock ?? null;

    if (currentConstantStock !== undefined) {
      stock = currentConstantStock;
    }

    return Object.values(DAY_OF_WEEK).reduce((acc, day) => {
      const typedDay = day as DayOfWeek;
      const isDisabled = isPriceEmptyForDay(typedDay);
      const originalDayConfig =
        stockConfiguration?.type === OPENING_CONFIGURATION_TYPE.DAILY
          ? stockConfiguration.days[typedDay]
          : undefined;
      const currentDayConfig = (currentStockConfiguration?.days?.[typedDay] ??
        originalDayConfig) as StockDayConfigurationFormValue | undefined;

      if (currentDayConfig) {
        const isClosedByHotel =
          currentDayConfig.isClosed && currentDayConfig.stock === 0;

        return {
          ...acc,
          [typedDay]: {
            ...currentDayConfig,
            isToggled: currentDayConfig.isToggled ?? !isClosedByHotel,
            isDisabled,
          },
        };
      }

      return {
        ...acc,
        [typedDay]: {
          stock: isDisabled ? 0 : stock,
          isClosed: isDisabled,
          isToggled: !isDisabled,
          isDisabled,
        },
      };
    }, {});
  };

  const handleTypeChange = (event: RadioChangeEvent) => {
    if (event.target.value !== OPENING_CONFIGURATION_TYPE.DAILY) {
      return;
    }

    const currentStockConfiguration = form.getFieldValue(
      STOCK_CONFIGURATION_FIELD
    ) as StockConfigurationFormValue | undefined;

    form.setFieldValue(STOCK_CONFIGURATION_FIELD, {
      type: OPENING_CONFIGURATION_TYPE.DAILY,
      days: buildDailyStockDays(currentStockConfiguration),
    });
  };

  return (
    <TextBlock
      title="Standard Stock"
      subtitle="Define the stock quantity that will be published each day for this activity."
      hasBorder
    >
      <TypeSelector
        name={['openingConfiguration', 'stockConfiguration', 'type']}
        constantDisabled={!!hasAdminDisabledDay}
        onChange={handleTypeChange}
      />
      {stockType === OPENING_CONFIGURATION_TYPE.CONSTANT && <ConstantStock />}
      {stockType === OPENING_CONFIGURATION_TYPE.DAILY && <DailyStock />}
    </TextBlock>
  );
};
